import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { DateValue, TimePicker } from "@mantine/dates";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import DatePickerInput from "@/components/atoms/DatePickerInput";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { classNames } from "@/utils/Classnames";
import {
  useGetCollectionDrivers,
  useGetCollectionSlots,
  putReassignDriver,
  putUpdatePickupDate,
} from "@/services/LogisticsCoordinator";
import { Collection } from "@/types";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { isSlotSelectable } from "@/utils/CollectionSlots";
import styles from "./styles.module.css";
import shared from "../styles.module.css";
import CollectionSlotSection from "../CollectionSlotSection";
import { Avatar, formatPlannedDateTime, SectionTitle, StatusBadge, initialsOf } from "../shared";

type Editing = null | "time" | "driver";

/** Mantine's TimePicker emits partial values while typing — only query on a complete HH:mm. */
const COMPLETE_TIME = /^\d{2}:\d{2}$/;

/**
 * Scheduled detail panel. `COMPLETED` collections are read-only. Date/time/driver changes hit the
 * transport endpoints and invalidate the collections query. Mounted with `key={collection.id}`
 * so the editing sub-panel resets when a different row is selected.
 */
const ScheduledDetail: FC<{ collection: Collection }> = ({ collection }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Editing>(null);
  const [chosenDate, setChosenDate] = useState<DateValue | undefined>(() =>
    collection.plannedDate ? new Date(collection.plannedDate) : null
  );
  const [chosenTime, setChosenTime] = useState(() =>
    collection.plannedDate ? dayjs(collection.plannedDate).format("HH:mm") : "08:00"
  );
  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(collection.receptionSlotId);

  const dayISO = chosenDate ? dayjs(chosenDate).format("YYYY-MM-DD") : "";
  const isLocked = collection.status === "COMPLETED";
  const canEdit = !isLocked;
  const editingTime = editing === "time";

  const { data: slotData, isFetching: slotsFetching } = useGetCollectionSlots(collection.containerId, dayISO, dayISO, {
    enabled: editingTime && !!dayISO,
  });
  const slots = slotData?.slots ?? [];
  const slotsLoading = slotsFetching || (editingTime && !slotData);

  /**
   * Does this site book slots? `receptionSlotId` is definitive — a collection can only hold a slot
   * at a site that publishes them. Otherwise fall back to the slot list: a site with no slot
   * configuration returns an empty one, so anything non-empty means slots were configured after
   * this collection was booked.
   */
  const slotMode = collection.receptionSlotId !== null || slots.length > 0;

  const plannedDateISO = dayISO && COMPLETE_TIME.test(chosenTime) ? dayjs(`${dayISO}T${chosenTime}`).toISOString() : "";
  const selectedSlot = slots.find((entry) => entry.slot.id === selectedSlotId) ?? null;
  const timeChosen = slotMode
    ? !!selectedSlot && isSlotSelectable(selectedSlot, { requireAvailableDriver: false })
    : !!plannedDateISO;

  // Non-slot sites only: "who's free if I move it here". Collection-keyed, so it excludes this
  // collection from the clash check and the current driver isn't filtered out by their own booking.
  const { data: moveDrivers } = useGetCollectionDrivers(collection.id, plannedDateISO, {
    enabled: editingTime && !slotMode && !!plannedDateISO,
  });
  const currentDriverBusy =
    !!moveDrivers && !moveDrivers.drivers.some((d) => d.userId === collection.assignedDriverUserId);

  // Swap driver, same window — no params, so it defaults to the collection's own window.
  const { data: driverData } = useGetCollectionDrivers(collection.id, undefined, {
    enabled: editing === "driver",
  });
  const drivers = driverData?.drivers ?? [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["getCollections"] });
    queryClient.invalidateQueries({ queryKey: ["collectionSlots"] });
    queryClient.invalidateQueries({ queryKey: ["collectionDrivers"] });
  };

  const handleDateChange = (value: DateValue | undefined) => {
    setChosenDate(value);
    // A slot id belongs to one day — drop it rather than re-booking another day's slot.
    setSelectedSlotId(null);
  };

  const updatePickupDate = useMutation({
    mutationFn: () =>
      putUpdatePickupDate(
        collection.id,
        slotMode && selectedSlotId !== null ? { receptionSlotId: selectedSlotId } : { plannedDate: plannedDateISO }
      ),
    onSuccess: (result) => {
      setEditing(null);
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("containers:toast.timeSaved", { day: formatPlannedDateTime(result.data.plannedDate) })
        )
      );
      invalidate();
    },
    onError: () => {
      notifications.show(ErrorNotificationObj(t("notifications:generic.error"), t("containers:toast.scheduleError")));
    },
  });

  const reassignDriver = useMutation({
    mutationFn: (userId: number) => putReassignDriver(collection.id, userId),
    onSuccess: (_data, userId) => {
      setEditing(null);
      const name = drivers.find((d) => d.userId === userId)?.name ?? "";
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("containers:toast.reassigned", { name }))
      );
      invalidate();
    },
    onError: () => {
      notifications.show(ErrorNotificationObj(t("notifications:generic.error"), t("containers:toast.assignError")));
    },
  });

  return (
    <aside className={shared.detailPanel}>
      <div className={shared.detailHeader}>
        {/* TODO: waste-type tile (abbreviation) — no abbr field from the API yet. */}
        <div className={shared.detailHeaderText}>
          <Text size="sm">
            {collection.wasteTypeName} · {collection.siteName}
          </Text>
          <div style={{ marginTop: 7 }}>
            <StatusBadge status={collection.status} />
          </div>
        </div>
      </div>

      <div className={shared.detailBody}>
        {/* Planned date & time */}
        <div className={shared.detailSectionHeader}>
          <SectionTitle>{t("containers:detail.plannedTime")}</SectionTitle>
          {canEdit && !editingTime && (
            <button type="button" className={shared.linkBtn} onClick={() => setEditing("time")}>
              {t("actions:change")}
            </button>
          )}
        </div>

        {!editingTime && (
          <div className={styles.slotCard} style={{ marginTop: 12 }}>
            <SVGAsset name="calendar" width={24} height={24} style={{ opacity: 0.85 }} />
            <Text size="md">{formatPlannedDateTime(collection.plannedDate)}</Text>
          </div>
        )}

        {editingTime && (
          <div className={shared.editPanel} style={{ marginTop: 12 }}>
            <DatePickerInput
              label={t("containers:detail.pickDate")}
              placeholder={t("actions:selectDate")}
              value={chosenDate}
              onChange={handleDateChange}
              minDate={new Date()}
            />

            {/* The site's arrival windows, or a free time entry when it publishes none. */}
            {(slotMode || slotsLoading) && (
              <CollectionSlotSection
                slots={slots}
                value={selectedSlotId}
                onChange={setSelectedSlotId}
                loading={slotsLoading}
                disabled={updatePickupDate.isPending}
                requireAvailableDriver={false}
              />
            )}

            {!slotMode && !slotsLoading && (
              <div style={{ marginTop: 16 }}>
                <SectionTitle>{t("containers:detail.pickTime")}</SectionTitle>
                <div style={{ marginTop: 10 }}>
                  <TimePicker
                    format="24h"
                    value={chosenTime}
                    onChange={setChosenTime}
                    disabled={updatePickupDate.isPending}
                  />
                </div>
              </div>
            )}

            <div className={styles.warningNote} style={{ marginTop: 14 }}>
              <SVGAsset name="info" width={15} height={15} style={{ marginTop: 1, opacity: 0.7 }} />
              <Text size="sm" color="tertiary" style={{ lineHeight: 1.45 }}>
                {currentDriverBusy
                  ? t("containers:detail.driverBusyAtNewTime", { name: collection.assignedDriverName })
                  : t("containers:detail.driverNotifiedTime")}
              </Text>
            </div>
            <div className={styles.editActions} style={{ marginTop: 14 }}>
              <CTA
                intent="primary"
                size="lg"
                style={{ flex: 1 }}
                disabled={!timeChosen || updatePickupDate.isPending}
                onClick={() => updatePickupDate.mutate()}
              >
                {t("containers:detail.saveNotify")}
              </CTA>
              <CTA intent="secondary" size="lg" onClick={() => setEditing(null)}>
                {t("actions:cancel")}
              </CTA>
            </div>
          </div>
        )}

        {/* Assigned driver */}
        <div className={shared.divider}>
          <div className={shared.detailSectionHeader}>
            <SectionTitle>{t("containers:detail.assignedDriver")}</SectionTitle>
            {canEdit && editing !== "driver" && (
              <button type="button" className={shared.linkBtn} onClick={() => setEditing("driver")}>
                {t("containers:detail.reassign")}
              </button>
            )}
          </div>

          {editing !== "driver" && (
            <div className={styles.slotCard} style={{ marginTop: 12 }}>
              <Avatar initials={initialsOf(collection.assignedDriverName)} size={38} />
              <div style={{ flex: 1 }}>
                <Text size="md" strong>
                  {collection.assignedDriverName}
                </Text>
                {/* TODO: truck · context line — not provided by the API yet. */}
              </div>
            </div>
          )}

          {editing === "driver" && (
            <div className={shared.driverList} style={{ marginTop: 12 }}>
              {drivers.map((d) => {
                const isCurrent = d.userId === collection.assignedDriverUserId;
                return (
                  <button
                    key={d.userId}
                    type="button"
                    disabled={reassignDriver.isPending}
                    onClick={() => reassignDriver.mutate(d.userId)}
                    className={classNames(shared.driverRow, isCurrent ? shared.driverRowRecommended : "")}
                  >
                    <Avatar initials={initialsOf(d.name)} size={34} />
                    <span className={shared.driverMeta}>
                      <span className={shared.driverName}>
                        <Text span size="sm" strong>
                          {d.name}
                        </Text>
                        {isCurrent && <span className={shared.driverTag}>{t("containers:driver.current")}</span>}
                      </span>
                    </span>
                    <Text span display="technical" size="xs" tt="uppercase" color="success">
                      {t("containers:driver.available")}
                    </Text>
                  </button>
                );
              })}
              <div className={styles.warningNote} style={{ marginTop: 4 }}>
                <SVGAsset name="info" width={15} height={15} style={{ marginTop: 1, opacity: 0.7 }} />
                <Text size="sm" color="tertiary" style={{ lineHeight: 1.45 }}>
                  {t("containers:detail.driverNotifiedReassign")}
                </Text>
              </div>
            </div>
          )}
        </div>

        {isLocked && (
          <div
            className={`${styles.resultBanner} ${styles.bannerCompleted}`}
            style={{ marginTop: 18, padding: "12px 14px" }}
          >
            <SVGAsset name="checkmark-green" width={16} height={16} />
            <Text size="sm" color="success">
              {t("containers:detail.completedLocked")}
            </Text>
          </div>
        )}
      </div>
    </aside>
  );
};

export default ScheduledDetail;
